<template>
  <div>
    <div class="vx-row" id="formulation_customer_related_data_form">
      <div class="vx-col w-full">
        <vx-card no-shadow>
          <template slot="actions">
            <vs-button v-if="$canAll(formulationPermissions)" @click="onClickSave()" class="mr-0" color="primary" size="small"
                       type="filled">Save Changes
            </vs-button>
          </template>

          <div class="vx-row mb-3">
            <div class="vx-col md:w-1/2 w-full md:mb-0">
              <ValidationProvider vid="commodity_code" name="commodity_code" rules="required"
                                  v-slot="{ errors }">
                <small class="label">Commodity code</small>
                <vs-input v-model="formulation.commodity_code" name="commodity_code" class="w-full" label=""
                          :danger="!!errors[0]" :disabled="!$canAll(formulationPermissions)"
                          :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
              </ValidationProvider>
            </div>
            <div class="vx-col md:w-1/2 w-full">
              <ValidationProvider vid="customer_project_brief_id" name="customer_project_brief_id" rules="" v-slot="{ errors }">
                <small class="label">Project brief</small>
                <v-select  :clearable="false" name="customer_project_brief_id" placeholder="" label="name" v-model="formulation.customer_project_brief_id" :reduce="option => option.id"
                           :options="project_briefs" :dir="$vs.rtl ? 'rtl' : 'ltr'" :disabled="!$canAll(formulationPermissions)">
                  <template v-slot:option="option">
                    <span>{{option.name}} - {{option.name}}</span>
                  </template>
                </v-select>
                <span class="text-danger text-sm">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="vx-row mb-3">
            <div class="vx-col md:w-1/2 w-full md:mb-0">
              <ValidationProvider vid="is_private" name="is_private" rules=""
                                  v-slot="{ errors }">
                <small class="label">Bespoke formulation?</small>
                <v-select  :clearable="false" name="is_private" placeholder="" label="name" v-on:input="onChangePrivateFormulation()"
                           v-model="formulation.is_private" :reduce="option => option.value" :options="[{ name: 'Yes', value: true }, { name: 'No', value: false }]" :dir="$vs.rtl ? 'rtl' : 'ltr'"
                           :disabled="!$canAll(formulationPermissions)"
                />
              </ValidationProvider>
            </div>
          </div>

        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import {RepositoryFactory} from '@/repository/RepositoryFactory'
import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'

import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

const FormulationsRepository = RepositoryFactory.get('formulations')
const CustomerRepository = RepositoryFactory.get('customer')
const DropDownRepository = RepositoryFactory.get('drop_down')

import moment from 'moment/src/moment'
import vSelect from "vue-select";

export default {
  name: "FormulationCustomerRelatedDataTab",
  data() {
    return {
      formulationId: '',
      project_briefs: [],
    }
  },
  props: {
    formulation_data: {
      type: Object,
      default: {},
    },
    formulationPermissions: {
      type: Array,
      default: [],
    }
  },
  methods: {
    async fetchProjectBrief() {
      await this.$vs.loading({
        container: '#formulation_customer_related_data_form',
        scale: 0.45
      })
      try {
        const {data} = await DropDownRepository.getProjectBriefDropDownList()
        this.project_briefs =  data.data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close('#formulation_customer_related_data_form > .con-vs-loading')
    },
    async onChangePrivateFormulation() {
      await this.$vs.loading({
        container: '#formulation_customer_related_data_form',
        scale: 0.45
      })
      try {
        const data = (await FormulationsRepository.changeFormulationPrivate(this.formulationId))
        await this.$emit('refreshGeneral', '')
        this.showSuccessMessage('Formulation Updated successfully')
      } catch (e) {
        this.formulation.is_private = !this.formulation.is_private
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close('#formulation_customer_related_data_form > .con-vs-loading')
    },
    async onClickSave() {
      await this.$vs.loading({
        container: '#formulation_customer_related_data_form',
        scale: 0.45
      })
      try {
        const payload = {
          commodity_code: this.formulation.commodity_code,
          customer_project_brief_id: this.formulation.customer_project_brief_id,
        }
        await FormulationsRepository.updateFormulationCustomerRelatedData(this.formulationId, payload)
        await this.$emit('refreshGeneral', '')
        this.showSuccessMessage('Formulation Updated successfully')
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close('#formulation_customer_related_data_form > .con-vs-loading')
    }
  },
  async mounted() {
    this.formulationId = this.$route.params.id
    await this.fetchProjectBrief()
  },
  computed: {
    formulation: function () {
      return this.formulation_data
    },
    mpPerKg() {
      // return Number((this.rmCost / this.formulation.batch_weight) + (this.totalOverHeads / this.formulation.batch_weight)).toFixed(2)
      return Number((this.totalOverHeads / Number(this.formulation.batch_weight)) + (this.rmCost / this.quantity)).toFixed(2)
    },
    labourTotal() {
      const labour_hours_minutes = moment.duration(this.formulation.number_of_hours).asMinutes()
      return Number(Number(this.formulation.labour_charge_per_hour) * Number(labour_hours_minutes / 60))
    },
    machineTotal() {
      const machine_hours_minutes = moment.duration(this.formulation.machine_number_of_hours).asMinutes()
      return Number(Number(this.formulation.machine_charge_per_hour) * Number(machine_hours_minutes / 60))
    },
    sundries() {
      return Number(this.formulation.sundry_cost)
    },
    totalOverHeads() {
      return Number(this.labourTotal + this.machineTotal + this.sundries)
    },
    rmCost() {
      const total = this.formulation.raw_material_items.reduce((acc, obj) => {
        return acc + Number(obj.quantity) * Number(obj.cost)
      }, 0)
      return Number(total)
    },
    quantity() {
      const total = this.formulation.raw_material_items.reduce((acc, obj) => {
        return acc + Number(obj.quantity)
      }, 0)
      return Number(total)
    },
  },
  mixins: [SuccessMessage, ErrorMessages],
  components: {
    flatPickr,
    'v-select': vSelect,
  },
}
</script>

<style scoped>

.__table-heading-small {
  /*font-size: x-small;*/
  /*font-weight: normal;*/
}

</style>
